import React from 'react';

import {
  FiEdit as EditIcon,
  FiNavigation as NavigationIcon,
} from 'react-icons/fi';
import { CopyIcon, TrashIcon } from 'src/components/Icons';
import { BaseActionsMenuButton, Action } from 'src/components/Dropdowns';
import { MemberActionsRendererProps } from 'src/components/AgGrid/Renderer/types';
import {
  PERM_MOD_SETTINGS_MEMBERS,
  UserRoles,
  USER_OPERATIONS,
} from 'src/constants';
import { useUserAccess } from 'src/hooks/useUserAccess';
import { usePlanInfo } from 'src/hooks/usePlanInfo';

export const MemberActionRenderer = ({
  data,
  handleChangeRole,
  handleDelete,
  handleResendInvite,
  handleCopyInvite,
  currentUser,
  onActionMenuClick,
}: MemberActionsRendererProps) => {
  const [actions, setActions] = React.useState<Action[]>([]);
  const { userRoles, userId: tableRowUserId } = data;
  const { userId: currentUserId } = currentUser || {};

  const canDeleteTeamMember = useUserAccess(
    PERM_MOD_SETTINGS_MEMBERS,
    USER_OPERATIONS.delete,
  );

  const { cancelAtPeriodEnd } = usePlanInfo();
  // for cancelled plans, disable team member delete option so that plan can not be modified
  const isDeleteButtonDisabled = cancelAtPeriodEnd;

  React.useEffect(() => {
    const actionsStaged: Action[] = [];

    if (userRoles && userRoles.length > 0 && tableRowUserId !== currentUserId) {
      switch (userRoles[0]) {
        case UserRoles.InternalAdmin:
          actionsStaged.push({
            name: 'Set as Staff',
            icon: <EditIcon />,
            onClick: () => handleChangeRole(data, UserRoles.InternalStaff),
          });
          break;
        case UserRoles.InternalStaff:
          actionsStaged.push({
            name: 'Set as Admin',
            icon: <EditIcon />,
            onClick: () => handleChangeRole(data, UserRoles.InternalAdmin),
          });
          break;
        default:
          break;
      }

      if (data.status === 'Invited' && handleResendInvite) {
        actionsStaged.push({
          name: 'Resend invite',
          icon: <NavigationIcon />,
          onClick: () => handleResendInvite(data),
        });
      }

      if (data.status !== 'Active' && handleCopyInvite) {
        actionsStaged.push({
          name: 'Copy invite link',
          onClick: () => handleCopyInvite(data),
          icon: <CopyIcon />,
        });
      }

      if (canDeleteTeamMember && handleDelete) {
        actionsStaged.push({
          name: 'Delete',
          icon: <TrashIcon />,
          onClick: () => handleDelete(data),
          isDelete: true,
          isDisabled: isDeleteButtonDisabled,
        });
      }
    }

    setActions(actionsStaged);
  }, [canDeleteTeamMember, data]);

  return (
    <BaseActionsMenuButton
      onActionMenuClick={onActionMenuClick}
      actions={actions}
    />
  );
};
